import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import type { Studio, StudioInsert } from "@/hooks/useStudios";

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  bridge_url: z
    .string()
    .url("Must be a valid URL")
    .optional()
    .or(z.literal("")),
  bridge_api_key: z.string().optional(),
  location: z.string().optional(),
  unity_hostname: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface StudioFormProps {
  studio?: Studio;
  onSubmit: (data: StudioInsert) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const StudioForm = ({
  studio,
  onSubmit,
  onCancel,
  isLoading,
}: StudioFormProps) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: studio?.name || "",
      description: studio?.description || "",
      bridge_url: studio?.bridge_url || "",
      bridge_api_key: studio?.bridge_api_key || "",
      location: studio?.location || "",
      unity_hostname: studio?.unity_hostname || "",
    },
  });

  const handleSubmit = (values: FormValues) => {
    onSubmit({
      name: values.name,
      description: values.description || null,
      bridge_url: values.bridge_url || null,
      bridge_api_key: values.bridge_api_key || null,
      location: values.location || null,
      unity_hostname: values.unity_hostname || null,
    });
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="space-y-4"
        data-testid="studio-form"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name *</FormLabel>
              <FormControl>
                <Input
                  placeholder="Studio A"
                  data-testid="studio-name-input"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="location"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Location</FormLabel>
              <FormControl>
                <Input
                  placeholder="Building 1, Floor 2"
                  data-testid="studio-location-input"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="bridge_url"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Bridge URL</FormLabel>
              <FormControl>
                <Input
                  placeholder="http://studio-a.local:8080"
                  data-testid="studio-bridge-url-input"
                  {...field}
                />
              </FormControl>
              <FormDescription>
                URL of the bridge server running in this studio
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="bridge_api_key"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Bridge API Key</FormLabel>
              <FormControl>
                <Input
                  type="password"
                  placeholder="Optional API key"
                  {...field}
                />
              </FormControl>
              <FormDescription>
                Optional authentication key for the bridge server
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="unity_hostname"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Unity Hostname</FormLabel>
              <FormControl>
                <Input placeholder="e.g. unity-server.local" {...field} />
              </FormControl>
              <FormDescription>
                Hostname of the Unity server used for intercom
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Description of the studio and its capabilities..."
                  rows={3}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isLoading}
            data-testid="studio-form-submit"
          >
            {studio ? "Update" : "Create"}
          </Button>
        </div>
      </form>
    </Form>
  );
};
